import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridCellParams, GridColDef, GridRowModel, useGridApiRef } from '@mui/x-data-grid';

import { useGetCharterFareFamilyPricing } from '~/hooks/apis/flights-charter/useCharterFareFamilyPricing';
import { useGetRewardsCatalogs } from '~/hooks/apis/rewards/useRewardsCatalogs';
import { useGetRewardsEntries, useUpsertRewardsEntries } from '~/hooks/apis/rewards/useRewardsEntries';

import type { FareFamilyPricingGetRes, FareFamilyPricingParams } from '~/services/CharterService';
import { formatDateSlashes } from '~/services/TimeService';

import ClassicRewardsInventoryDialog from './ClassicRewardsInventoryDialog';
import {
  CABIN_CLASSES,
  CABIN_CLASS_LABELS,
  type ClassicRewardsPricingRow,
  type ClassicRewardsSearchFilters,
  type FareFamilyCabinClass,
  buildPricingRows,
} from './classicRewards.types';

/**
 * Charter flight route pair options — currently hard-coded to MEL↔MLE for v1.
 *
 * TODO: Replace with a fetched list from svc-flights or a shared constant once
 * we support more than one charter route. Until then, edits here need to land
 * in lockstep with charter availability.
 */
const ROUTE_OPTIONS = [
  {
    label: 'MEL → MLE',
    departingDepAirport: 'MEL',
    departingArrAirport: 'MLE',
    returningDepAirport: 'MLE',
    returningArrAirport: 'MEL',
  },
] as const;

/** Page size for charter fare-family pricing fetch — matches API default. */
const CHARTER_FETCH_LIMIT = 200;

/** Available currencies for reward entry pricing. */
const CURRENCY_OPTIONS = ['AUD', 'USD', 'NZD'] as const;

/** Default currency for v1 (Australia-based charter flights). */
const DEFAULT_CURRENCY = 'AUD';

/** Status options for entries. */
const STATUS_OPTIONS = [
  { label: 'Active', value: 'ACTIVE' as const },
  { label: 'Inactive', value: 'INACTIVE' as const },
];

/** Type for fare-family pricing API result item. */
type FareFamilyResult = FareFamilyPricingGetRes['result'][number];

/** Formats a date string for display. */
function formatDisplayDate(value: string): string {
  return formatDateSlashes(value) || value;
}

/**
 * Admin page for configuring classic reward pricing per flight leg + cabin class.
 *
 * Lists flight instances from the charter fare-family pricing API, then overlays
 * reward entries from svc-lux-loyalty. Supports inline editing and bulk save.
 */
export default function ClassicRewardsPage(): React.JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const gridApiRef = useGridApiRef();

  // --- Form state ---
  const [selectedRoute, setSelectedRoute] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState<string>(DEFAULT_CURRENCY);
  const [selectedCabinClasses, setSelectedCabinClasses] = useState<Array<FareFamilyCabinClass>>([...CABIN_CLASSES]);

  // --- Search state ---
  // `searchFilters` captures the route + currency the user clicked Search with;
  // it drives the charter fare-family query below. Storing it as state (rather
  // than reading the form values directly) means the table reflects the
  // executed search, not pending edits to the filter form.
  const [searchFilters, setSearchFilters] = useState<ClassicRewardsSearchFilters | null>(null);

  // Charter fare-family pricing query — driven by `searchFilters`. Using
  // TanStack Query gives us automatic dedup/cancellation via `signal`, so
  // rapid Search clicks don't race (the latest click always wins).
  const charterParams: FareFamilyPricingParams | null = searchFilters
    ? {
        departingDepAirport: searchFilters.departingDepAirport,
        departingArrAirport: searchFilters.departingArrAirport,
        returningDepAirport: searchFilters.returningDepAirport,
        returningArrAirport: searchFilters.returningArrAirport,
        limit: CHARTER_FETCH_LIMIT,
      }
    : null;
  const charterQuery = useGetCharterFareFamilyPricing(charterParams);
  const fareFamilyData: Array<FareFamilyResult> = charterQuery.data ?? [];

  // --- Edit state ---
  const [editedRows, setEditedRows] = useState<Map<string, ClassicRewardsPricingRow>>(new Map());

  // --- Inventory dialog state ---
  const [inventoryEntryId, setInventoryEntryId] = useState<string | null>(null);
  const [inventoryEntryLabel, setInventoryEntryLabel] = useState('');

  // --- Step 1: Fetch the flights catalog by itemType ---
  const { data: catalogs } = useGetRewardsCatalogs({ itemType: 'flight', status: 'ACTIVE' });
  const flightsCatalogId = catalogs?.[0]?.id ?? null;

  // --- Step 2: Collect journeyKeys from flight data for querying entries ---
  const journeyKeys = useMemo(() => {
    if (!fareFamilyData.length) {
      return [];
    }
    const keys: Array<string> = [];
    for (const flight of fareFamilyData) {
      for (const cabinClassKey of selectedCabinClasses) {
        const tier = flight.tiers[cabinClassKey];
        if (tier?.journeyKey) {
          keys.push(tier.journeyKey);
        }
      }
    }
    return keys;
  }, [fareFamilyData, selectedCabinClasses]);

  // --- Step 3: Fetch reward entries matching the journeyKeys ---
  const entriesQuery = useGetRewardsEntries(
    flightsCatalogId && journeyKeys.length > 0
      ? {
          catalogId: flightsCatalogId,
          itemType: 'flight',
          itemSubType: 'charter',
          itemReferences: journeyKeys,
        }
      : null,
  );

  const upsertEntriesMutation = useUpsertRewardsEntries();

  // --- Merge flight data + entries into grid rows ---
  const baseRows = useMemo(() => {
    if (!fareFamilyData.length) {
      return [];
    }

    const filteredFlights = fareFamilyData.map((flight) => ({
      ...flight,
      tiers: Object.fromEntries(
        selectedCabinClasses.map((key) => [key, flight.tiers[key]]),
      ) as FareFamilyResult['tiers'],
    }));

    return buildPricingRows(filteredFlights, entriesQuery.data ?? [], selectedCurrency);
  }, [fareFamilyData, entriesQuery.data, selectedCurrency, selectedCabinClasses]);

  /** Map of base rows by journeyKey for diff tracking. */
  const baseRowMap = useMemo(() => {
    const map = new Map<string, ClassicRewardsPricingRow>();
    for (const row of baseRows) {
      map.set(row.journeyKey, row);
    }
    return map;
  }, [baseRows]);

  /** Grid rows = base rows with edited rows overlaid. */
  const rows = useMemo(() => {
    return baseRows.map((baseRow) => editedRows.get(baseRow.journeyKey) ?? baseRow);
  }, [baseRows, editedRows]);

  // --- Handlers ---

  /**
   * Updates `searchFilters` with the form's current values, which triggers the
   * charter fare-family query (and downstream rewards entries query) via
   * TanStack Query. Clears any pending edits so they don't apply to the new
   * result set.
   */
  const handleSearch = useCallback(() => {
    const route = ROUTE_OPTIONS[selectedRoute];
    if (!route) {
      return;
    }

    setEditedRows(new Map());
    setSearchFilters({
      departingDepAirport: route.departingDepAirport,
      departingArrAirport: route.departingArrAirport,
      returningDepAirport: route.returningDepAirport,
      returningArrAirport: route.returningArrAirport,
      cashCurrency: selectedCurrency,
    });
  }, [selectedRoute, selectedCurrency]);

  /** Tracks cell edits, clamps numeric values to >= 0, and compares against the base row. */
  const processRowUpdate = useCallback(
    (newRow: GridRowModel<ClassicRewardsPricingRow>) => {
      // Clamp numeric fields to prevent negative values
      const clamped = {
        ...newRow,
        pointsPrice: Math.max(0, newRow.pointsPrice ?? 0),
        cashPrice: Math.max(0, newRow.cashPrice ?? 0),
      };

      setEditedRows((current) => {
        const original = baseRowMap.get(clamped.journeyKey);
        const next = new Map(current);

        const hasChanges =
          !original ||
          clamped.pointsPrice !== original.pointsPrice ||
          clamped.cashPrice !== original.cashPrice ||
          clamped.status !== original.status;

        if (hasChanges) {
          next.set(clamped.journeyKey, clamped);
        } else {
          next.delete(clamped.journeyKey);
        }

        return next;
      });

      return clamped;
    },
    [baseRowMap],
  );

  /** Highlights cells that differ from the base row. */
  const getCellClassName = useCallback(
    (params: GridCellParams<ClassicRewardsPricingRow>) => {
      const editableFields = ['pointsPrice', 'cashPrice', 'status'];
      if (!editableFields.includes(params.field)) {
        return '';
      }

      const original = baseRowMap.get(params.row.journeyKey);
      if (!original) {
        return '';
      }

      const originalValue = original[params.field as keyof ClassicRewardsPricingRow];
      return params.value !== originalValue ? 'cell-modified' : '';
    },
    [baseRowMap],
  );

  /** Enters cell edit mode on click (single-click editing). */
  const handleCellClick = useCallback(
    (params: GridCellParams) => {
      if (!params.isEditable) {
        return;
      }
      const cellMode = gridApiRef.current.getCellMode(params.id, params.field);
      if (cellMode === 'view') {
        gridApiRef.current.startCellEditMode({ id: params.id, field: params.field });
      }
    },
    [gridApiRef],
  );

  /** Saves all edited rows by bulk upserting entries in svc-lux-loyalty. */
  const handleSave = useCallback(async () => {
    if (!flightsCatalogId || editedRows.size === 0) {
      return;
    }

    // Split edited rows into "to save" and "skipped because both prices are 0".
    // 0/0 is treated as invalid (a reward with no cost AND no points isn't a
    // reward), but we surface skipped rows to the user instead of silently
    // dropping them — otherwise highlighted "edited" rows can disappear on
    // save with no feedback.
    const allEdits = Array.from(editedRows.values());
    const skippedRows = allEdits.filter((row) => row.pointsPrice <= 0 && row.cashPrice <= 0);
    const entries = allEdits
      .filter((row) => row.pointsPrice > 0 || row.cashPrice > 0)
      .map((row) => ({
        itemType: 'flight' as const,
        itemSubType: 'charter',
        itemReference: row.journeyKey,
        itemSubReference: '',
        pointsPrice: row.pointsPrice,
        cashPrice: row.cashPrice,
        cashCurrency: row.cashCurrency,
        // `NOT_CONFIGURED` was filtered out of the status dropdown, but guard
        // here in case a legacy edited-row state still carries it.
        status: row.status === 'NOT_CONFIGURED' ? ('ACTIVE' as const) : row.status,
      }));

    if (entries.length === 0) {
      enqueueSnackbar('No valid entries to save — points price or cash price must be greater than 0.', {
        variant: 'warning',
      });
      return;
    }

    try {
      await upsertEntriesMutation.mutateAsync({ catalogId: flightsCatalogId, entries });
      if (skippedRows.length > 0) {
        enqueueSnackbar(
          `${entries.length} entries saved. ${skippedRows.length} skipped — points price and cash price were both 0.`,
          { variant: 'warning' },
        );
      } else {
        enqueueSnackbar(`${entries.length} reward entries saved successfully.`, { variant: 'success' });
      }
      // Clear successfully-saved edits but keep the skipped rows so the user
      // can see and correct them.
      setEditedRows((current) => {
        const next = new Map<string, ClassicRewardsPricingRow>();
        for (const row of skippedRows) {
          next.set(row.journeyKey, current.get(row.journeyKey) ?? row);
        }
        return next;
      });
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to save reward entries.', { variant: 'error' });
    }
  }, [flightsCatalogId, editedRows, upsertEntriesMutation, enqueueSnackbar]);

  // --- Grid columns ---
  const columns = useMemo<Array<GridColDef<ClassicRewardsPricingRow>>>(
    () => [
      {
        field: 'departureDate',
        headerName: 'Departure',
        width: 120,
        valueFormatter: (value) => formatDisplayDate(String(value)),
      },
      {
        field: 'route',
        headerName: 'Route',
        width: 110,
      },
      {
        field: 'cabinClass',
        headerName: 'Cabin',
        width: 160,
      },
      {
        field: 'pointsPrice',
        headerName: 'Points Price',
        width: 130,
        type: 'number',
        editable: true,
        align: 'right',
        headerAlign: 'right',
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: params.props.value != null && params.props.value < 0,
        }),
      },
      {
        field: 'cashPrice',
        headerName: 'Cash Gap ($)',
        width: 130,
        type: 'number',
        editable: true,
        align: 'right',
        headerAlign: 'right',
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: params.props.value != null && params.props.value < 0,
        }),
      },
      {
        field: 'cashCurrency',
        headerName: 'Currency',
        width: 90,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        // Only allow selecting real backend statuses. `NOT_CONFIGURED` is a
        // synthetic display-only value for unconfigured rows; if it were in
        // `valueOptions` users could pick it but the save handler would
        // silently coerce it to ACTIVE — confusing UX.
        editable: true,
        type: 'singleSelect',
        valueOptions: STATUS_OPTIONS.map((opt) => opt.value),
        renderCell: (params) => {
          if (params.value === 'NOT_CONFIGURED') {
            return <Chip label="Not Configured" size="small" variant="outlined" />;
          }
          if (params.value === 'ACTIVE') {
            return <Chip label="Active" size="small" color="success" />;
          }
          return <Chip label="Inactive" size="small" color="default" />;
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (params) =>
          params.row.entryId ? (
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setInventoryEntryId(params.row.entryId);
                setInventoryEntryLabel(`${params.row.route} ${params.row.departureDate} — ${params.row.cabinClass}`);
              }}
            >
              Inventory
            </Button>
          ) : null,
      },
    ],
    [],
  );

  const isLoading = charterQuery.isFetching || entriesQuery.isFetching;

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h4" fontWeight={600}>
          Classic Rewards
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure reward pricing (points + cash gap) per charter flight leg and cabin class.
        </Typography>
      </Stack>

      {/* --- Search filters --- */}
      <Card>
        <CardHeader title="Filters" sx={{ pb: 0 }} />
        <CardContent>
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Route"
                  value={selectedRoute}
                  onChange={(e) => setSelectedRoute(Number(e.target.value))}
                >
                  {ROUTE_OPTIONS.map((route, index) => (
                    <MenuItem key={index} value={index}>
                      {route.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Currency"
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                >
                  {CURRENCY_OPTIONS.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  select
                  SelectProps={{
                    multiple: true,
                    value: selectedCabinClasses,
                    renderValue: (selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as Array<FareFamilyCabinClass>).map((value) => (
                          <Chip key={value} size="small" label={CABIN_CLASS_LABELS[value]} />
                        ))}
                      </Box>
                    ),
                  }}
                  fullWidth
                  size="small"
                  label="Cabin classes"
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedCabinClasses(
                      typeof value === 'string'
                        ? (value.split(',') as Array<FareFamilyCabinClass>)
                        : (value as Array<FareFamilyCabinClass>),
                    );
                  }}
                >
                  {CABIN_CLASSES.map((key) => (
                    <MenuItem key={key} value={key}>
                      {CABIN_CLASS_LABELS[key]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={isLoading || selectedCabinClasses.length === 0}
              >
                Search
              </Button>
              <LoadingButton
                variant="contained"
                loading={upsertEntriesMutation.isPending}
                onClick={handleSave}
                disabled={editedRows.size === 0 || !flightsCatalogId}
              >
                Save ({editedRows.size})
              </LoadingButton>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* --- Error / empty states --- */}
      {charterQuery.error && (
        <Alert severity="error">
          {charterQuery.error instanceof Error ? charterQuery.error.message : 'Failed to fetch flight data.'}
        </Alert>
      )}
      {entriesQuery.error && <Alert severity="error">{entriesQuery.error.message}</Alert>}

      {searchFilters && !isLoading && rows.length === 0 && (
        <Alert severity="info">No flight data found for the selected route. Check that flights are configured.</Alert>
      )}

      {!flightsCatalogId && catalogs && catalogs.length === 0 && (
        <Alert severity="warning">
          No active flight rewards catalog found. Create one in svc-lux-loyalty before configuring entries.
        </Alert>
      )}

      {/* --- Data grid --- */}
      {searchFilters && (
        <Box sx={{ width: '100%' }}>
          <DataGrid
            apiRef={gridApiRef}
            rows={rows}
            columns={columns}
            loading={isLoading}
            autoHeight
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 50 } },
              sorting: { sortModel: [{ field: 'departureDate', sort: 'asc' }] },
            }}
            disableRowSelectionOnClick
            onCellClick={handleCellClick}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(error) =>
              enqueueSnackbar(error instanceof Error ? error.message : 'Failed to update cell.', { variant: 'error' })
            }
            getCellClassName={getCellClassName}
            sx={{ '& .cell-modified': { bgcolor: 'warning.light' } }}
          />
        </Box>
      )}

      {/* --- Inventory dialog --- */}
      <ClassicRewardsInventoryDialog
        open={Boolean(inventoryEntryId)}
        onClose={() => setInventoryEntryId(null)}
        entryId={inventoryEntryId}
        entryLabel={inventoryEntryLabel}
      />
    </Stack>
  );
}
